import {
  isEqualTrackRef,
  isTrackReference,
  isWeb,
  log,
} from "@livekit/components-core";
import {
  CarouselLayout,
  Chat,
  ConnectionStateToast,
  FocusLayoutContainer,
  GridLayout,
  LayoutContextProvider,
  RoomAudioRenderer,
  useCreateLayoutContext,
  usePinnedTracks,
  useTracks,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import * as React from "react";
import { createPortal } from "react-dom";
import { LiveInterviewControlBar } from "./LiveInterviewControlBar";
import {
  LiveInterviewFocusLayout,
  LiveInterviewParticipantTile,
} from "./LiveInterviewParticipantTile";
import { LiveInterviewPresentingPortal } from "./LiveInterviewPresentingPortal";
import { LiveInterviewPresentingStage } from "./LiveInterviewPresentingStage";
import { LocalScreenSharePresentationGuard } from "./LocalScreenSharePresentationGuard";
import { resolveParticipantDisplayName } from "../utils/liveInterviewDisplayUtils";
import {
  getRemoteScreenShareTrack,
  isScreenShareTrackRef,
} from "../utils/liveInterviewTrackUtils";
import {
  LiveInterviewRaiseHandBanner,
  LiveInterviewRaiseHandProvider,
} from "../utils/liveInterviewRaiseHand";

/**
 * Same layout as LiveKit {@link VideoConference}, but uses {@link LiveInterviewControlBar}
 * Screen share: exclude meeting tab in Chrome picker; local sharer sees a static
 * presenting tile instead of live capture (prevents hall-of-mirrors on entire screen).
 */
export function LiveInterviewVideoConference({
  chatMessageFormatter,
  chatMessageDecoder,
  chatMessageEncoder,
  SettingsComponent,
  onLeave,
  leaveDisabled = false,
  leaveLabel = "Leave or end meeting",
  meetingMenuProps,
  ...props
}) {
  const [widgetState, setWidgetState] = React.useState({
    showChat: false,
    unreadMessages: 0,
    showSettings: false,
  });
  const lastAutoFocusedScreenShareTrack = React.useRef(null);

  const tracks = useTracks([
    { source: Track.Source.Camera, withPlaceholder: true },
    { source: Track.Source.ScreenShare, withPlaceholder: false },
  ]);

  const widgetUpdate = (state) => {
    log.debug("updating widget state", state);
    setWidgetState(state);
  };

  const layoutContext = useCreateLayoutContext();

  const screenShareTracks = tracks.filter(isScreenShareTrackRef);

  const localIsPresenting = screenShareTracks.some(
    (track) => track.participant.isLocal && Boolean(track.publication?.trackSid),
  );

  const remoteScreenShare = getRemoteScreenShareTrack(tracks);
  const remotePresenterName = remoteScreenShare
    ? resolveParticipantDisplayName(remoteScreenShare.participant)
    : "";

  const layoutTracks = React.useMemo(
    () =>
      tracks.filter((track) => {
        if (!isScreenShareTrackRef(track)) {
          return true;
        }
        if (track.participant.isLocal) {
          return false;
        }
        if (localIsPresenting) {
          return false;
        }
        return true;
      }),
    [localIsPresenting, tracks],
  );

  const focusTrack = usePinnedTracks(layoutContext)?.[0];
  const activeFocusTrack =
    localIsPresenting ||
    (focusTrack && isScreenShareTrackRef(focusTrack) && focusTrack.participant?.isLocal)
      ? undefined
      : focusTrack;
  const carouselTracks = layoutTracks.filter((track) => !isEqualTrackRef(track, activeFocusTrack));

  const screenShareTracksDependencyKey = screenShareTracks
    .map((ref) => `${ref.publication.trackSid}_${ref.publication.isSubscribed}`)
    .join();

  React.useEffect(() => {
    if (localIsPresenting) {
      if (
        lastAutoFocusedScreenShareTrack.current ||
        (focusTrack && isScreenShareTrackRef(focusTrack))
      ) {
        layoutContext.pin.dispatch?.({ msg: "clear_pin" });
        lastAutoFocusedScreenShareTrack.current = null;
      }
      return;
    }

    if (remoteScreenShare) {
      const pinnedSid = lastAutoFocusedScreenShareTrack.current?.publication?.trackSid;
      const nextSid = remoteScreenShare.publication.trackSid;
      if (pinnedSid !== nextSid) {
        log.debug("Auto set screen share focus:", { newScreenShareTrack: remoteScreenShare });
        layoutContext.pin.dispatch?.({ msg: "set_pin", trackReference: remoteScreenShare });
        lastAutoFocusedScreenShareTrack.current = remoteScreenShare;
      }
      return;
    }

    if (
      lastAutoFocusedScreenShareTrack.current &&
      !screenShareTracks.some(
        (track) =>
          track.publication.trackSid ===
          lastAutoFocusedScreenShareTrack.current?.publication?.trackSid,
      )
    ) {
      log.debug("Auto clearing screen share focus.");
      layoutContext.pin.dispatch?.({ msg: "clear_pin" });
      lastAutoFocusedScreenShareTrack.current = null;
    }
    if (focusTrack && !isTrackReference(focusTrack)) {
      const updatedFocusTrack = tracks.find(
        (tr) =>
          tr.participant.identity === focusTrack.participant.identity &&
          tr.source === focusTrack.source,
      );
      if (updatedFocusTrack !== focusTrack && isTrackReference(updatedFocusTrack)) {
        layoutContext.pin.dispatch?.({ msg: "set_pin", trackReference: updatedFocusTrack });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- LiveKit VideoConference parity (narrow deps)
  }, [localIsPresenting, remoteScreenShare?.publication?.trackSid, screenShareTracksDependencyKey, focusTrack?.publication?.trackSid, tracks]);

  return (
    <div
      className={`lk-video-conference${localIsPresenting ? " is-local-presenting" : ""}`}
      {...props}
    >
      {isWeb() && (
        <LayoutContextProvider
          value={layoutContext}
          onWidgetChange={widgetUpdate}
        >
          <LocalScreenSharePresentationGuard />
          <LiveInterviewPresentingPortal active={localIsPresenting} />
          <LiveInterviewRaiseHandProvider>
          <div className="lk-video-conference-inner">
            {localIsPresenting ? (
              <LiveInterviewPresentingStage
                title="You're presenting to everyone"
                subtitle="Others can see your screen. Stop share from the control bar when you're done."
              />
            ) : !activeFocusTrack ? (
              <div className="lk-grid-layout-wrapper">
                <GridLayout tracks={layoutTracks}>
                  <LiveInterviewParticipantTile />
                </GridLayout>
              </div>
            ) : (
              <div className="lk-focus-layout-wrapper">
                {remoteScreenShare && isScreenShareTrackRef(activeFocusTrack) ? (
                  <div className="li-remote-presenting-banner" role="status" aria-live="polite">
                    <span className="li-remote-presenting-banner-title">
                      {remotePresenterName} is presenting
                    </span>
                    <span className="li-remote-presenting-banner-sub">Screen share</span>
                  </div>
                ) : null}
                <FocusLayoutContainer>
                  <CarouselLayout tracks={carouselTracks}>
                    <LiveInterviewParticipantTile />
                  </CarouselLayout>
                  {activeFocusTrack ? (
                    <LiveInterviewFocusLayout trackRef={activeFocusTrack} />
                  ) : null}
                </FocusLayoutContainer>
              </div>
            )}
            {localIsPresenting ? null : remoteScreenShare && !activeFocusTrack ? (
              <div className="li-presenting-banner" role="status" aria-live="polite">
                <span className="li-presenting-banner-title">
                  {remotePresenterName} is presenting their screen
                </span>
                <span className="li-presenting-banner-sub">Screen share is active in this meeting.</span>
              </div>
            ) : null}
            <LiveInterviewRaiseHandBanner />
            <LiveInterviewControlBar
              controls={{ chat: false, settings: false, leave: true }}
              onLeave={onLeave}
              leaveDisabled={leaveDisabled}
              leaveLabel={leaveLabel}
              meetingMenu={meetingMenuProps}
            />
          </div>
          </LiveInterviewRaiseHandProvider>
          <Chat
            style={{ display: widgetState.showChat ? "grid" : "none" }}
            messageFormatter={chatMessageFormatter}
            messageEncoder={chatMessageEncoder}
            messageDecoder={chatMessageDecoder}
          />
          {SettingsComponent && widgetState.showSettings
            ? createPortal(
                <div
                  className="lk-settings-menu-modal is-open"
                  onClick={(event) => {
                    if (event.target === event.currentTarget) {
                      widgetUpdate({ ...widgetState, showSettings: false });
                    }
                  }}
                >
                  <SettingsComponent />
                </div>,
                document.body,
              )
            : null}
        </LayoutContextProvider>
      )}
      <RoomAudioRenderer />
      <ConnectionStateToast />
    </div>
  );
}
